import { useQuery } from "@tanstack/react-query";
import { authService } from "@/services/AuthService";
import { apiRequest } from "@/lib/queryClient";
import { logger } from "@/lib/logger";

export interface OrderHistoryItem {
  id: string | number; // API returns string hash like "67dad5895d82fd6b26c4a0b67dff636173a70968"
  restaurant: {
    id?: number; // Numeric Eatzy restaurant ID (e.g., 462076)
    name: string;
    logo?: string;
    client?: string;
    profile?: string; // Eatzy client ID - API returns this field for restaurant identification
  };
  items: Array<{
    id?: number; // Order-specific item instance ID
    item_id?: string; // Menu item ID (e.g., "I463973") - used to link reviews to menu items
    name: string;
    quantity: number;
    price?: number;
    options?: Array<{
      id: number;
      order_item_id: string;
      question_id: string;
      question_text: string;
      quantity: number;
      answer_id: string;
      answer_text: string;
    }>;
  }>;
  status: string;
  date: number; // Unix timestamp
  total: number; // In dollars
  type?: "delivery" | "takeout"; // API returns "type" not "mode"
  mode?: "delivery" | "takeout"; // Legacy field - prefer "type"
  address?: {
    id?: number;
    address?: string; // API returns "address" field (street address)
    street?: string; // Legacy field
    city?: string;
    state?: string;
    zipcode?: string; // API returns "zipcode" not "zip"
    zip?: string; // Legacy field
    latitude?: string;
    longitude?: string;
  };
}

export interface OrderHistoryResponse {
  total_results: number;
  total_pages: number;
  page: number;
  results: OrderHistoryItem[];
}

export class EatzyTokenExpiredError extends Error {
  readonly eatzyTokenExpired = true;
  constructor() {
    super(
      "Eatzy session expired — please sign in again to see your order history."
    );
    this.name = "EatzyTokenExpiredError";
  }
}

async function fetchOrderHistory(
  page: number = 1,
  query: string = ""
): Promise<OrderHistoryResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    query: query,
  });

  const response = await authService.eatzyApiCall(
    `/consumer/customer/order/history?${params.toString()}`
  );

  if (!response.ok) {
    if (response.status === 403) {
      try {
        const errorData = await response.clone().json();
        if (errorData.eatzyTokenExpired) throw new EatzyTokenExpiredError();
      } catch (e) {
        if (e instanceof EatzyTokenExpiredError) throw e;
      }
    }
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch order history: ${response.status} - ${errorText}`
    );
  }

  const data = await response.json();

  // Fire-and-forget: notify server to process completed orders for Kudoz
  // Server-side deduplication (kudozTriggerEvents) prevents double-awarding
  if (Array.isArray(data?.results) && data.results.length > 0) {
    apiRequest("POST", "/api/kudoz/process-order-history", {
      orders: data.results,
    }).catch(() => {});
  }

  // Debug log to see actual API response structure
  if (data.results && data.results.length > 0) {
    logger.log("[useOrderHistory] Sample order restaurant data:", {
      restaurant: data.results[0].restaurant,
      hasProfile: !!data.results[0].restaurant?.profile,
      hasClient: !!data.results[0].restaurant?.client,
      hasId: !!data.results[0].restaurant?.id,
    });

    // Log all unique order statuses to understand available values
    const allStatuses = data.results.map(
      (order: OrderHistoryItem) => order.status
    );
    const uniqueStatuses = Array.from(new Set(allStatuses));
    logger.log("[useOrderHistory] Order statuses found:", {
      uniqueStatuses,
      allStatusesByOrder: data.results.map((order: OrderHistoryItem) => ({
        id: order.id,
        status: order.status,
        restaurantName: order.restaurant.name,
        date: new Date(order.date * 1000).toLocaleDateString(),
      })),
    });
  }

  return data;
}

export function useOrderHistory(
  page: number = 1,
  query: string = "",
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["/api/eatzy/consumer/customer/order/history", page, query],
    queryFn: () => fetchOrderHistory(page, query),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

export function formatOrderDate(timestamp: number): string {
  const orderDate = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - orderDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 14) {
    return "1 week ago";
  } else if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)} weeks ago`;
  } else if (diffDays < 60) {
    return "1 month ago";
  } else {
    return orderDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}

export function formatOrderTotal(total: number): string {
  // API returns total in dollars (not cents)
  return `$${total.toFixed(2)}`;
}
